<template>
    <Header style="background:white;border-bottom-right-radius:3rem;border-bottom-left-radius:3rem;" />
    <div class="privacy-container">
        <div class="title">
            <h1>Privacy Policy</h1>
        </div>
        <div class="info">
            <h1>Shabeb Delivery recognizes and respects the importance of maintaining the privacy of our customers, registered members, and vendors, and has established this Privacy Policy consequently. The information you will find in this Privacy Policy includes:</h1>
            <ul>
                <li>the personal information we may collect from you when you visit our website, respond or subscribe to our emails, use our mobile applications, place orders through social media applications (such as WhatsApp or Facebook Messenger), or otherwise contact us via telephone, text (SMS), or email,</li>
                <li>why we gather information from you,</li>
                <li>how we collect it,</li>
                <li>how we use it (including with whom we may share it), and</li>
                <li>the choices you have regarding our use of, and your access to and correction of, personal information you have provided.</li>
            </ul>
            <h1>This Privacy Policy is part of our <router-link to="/terms">Terms of Use</router-link>, which governs the use of our website located at <router-link to="/"> https://shabeb.com/</router-link> and mobile applications. By submitting personal information through the Shabeb Delivery website, Shabeb Delivery mobile applications, or Shabeb Delivery services, you provide consent to the terms of this Privacy Policy and you expressly consent to the collection, use, and disclosure of your personal information in accordance with this Privacy Policy.</h1>
        </div>
        <div class="policies">
            <div class="policy">
                <h1>1.Information We Collect</h1>
                <p>The following information is collected by or on behalf of Shabeb Delivery:</p>
                <ul>
                    <li>User Profile Information: We collect and store this vendor or driver information when you create or update your Shabeb Delivery account or subscribe to our email. This may include your name, business name, [personal or business] email, [personal or business] phone number, login name and password, location information, and payment or banking information (including related payment verification information).* Please note that Shabeb Delivery cannot be used by minors (the age depends on where you reside) without the supervision of a parent or guardian. Shabeb Delivery is not liable for credit/debit card transactions performed by minors without the consent of a parent or guardian.</li>
                    <li>
                        <p>User Transaction Information: We collect and store this transaction data when you provide or update your Shabeb Delivery transaction data or when a transaction is completed. This may include:</p>
                        <ul>
                            <li>your billing information (credit/debit card number, expiration date, CVV code, alternate or additional billing information and billing address),</li>
                            <li>order history and details,</li>
                            <li>date and time of delivery,</li>
                            <li>business receipt data,</li>
                            <li>any promotion, referral code, and related information,</li>
                            <li>recipients’ names, addresses, telephone numbers, and email addresses,</li>
                            <li>network information – including IP address, network carrier and country code, browsing information, such as how you interact with our services, and</li>
                            <li>other information you provide to us, including User Submissions (defined in the Terms of Use).</li>
                        </ul>
                    </li>
                    <li>Shabeb Business Analytics Information: In order to provide our customers with powerful and valuable information, Shabeb Delivery saves all historical transactions to enable advanced analytics. This information allows each business or user to track: their order volume, peak order times, and order item frequency (beta), across a specific time range. Business owners can use this data to plan and forecast. These analytics are separate from Google Analytics, which are explained further in section 4 of this Privacy Policy.</li>
                </ul>
            </div>
            <div class="policy">
                <h1>2.Why We Collect Information from You</h1>
                <p>In brief, Shabeb Delivery gathers personal information mainly to communicate with you and to process your orders, but additionally to help improve our products and customer service, to provide an enhanced and more personalized delivery experience and to inform vendors and drivers of special offers, updates, contests, and more.</p>
                <p>The purposes for gathering information from you include:</p>
                <ul>
                    <li>
                        <p><span>Providing services and features:</span> Shabeb Delivery collects and uses the necessary information to provide you with its services and features. Shabeb Delivery uses this information to:</p>
                        <ul>
                            <li>Create and update your Shabeb Delivery driver or vendor account,</li>
                            <li>Verify your identity,</li>
                            <li>Provide you with available services based on your location details,</li>
                            <li>Enable the delivery of your order,</li>
                            <li>Process or facilitate payments of certain transactions,</li>
                            <li>Enable features to personalize your Shabeb Delivery account,</li>
                            <li>Send you promotional information, and</li>
                            <li>Perform internal operations necessary to provide our services, including to troubleshoot bugs and operational problems, to conduct data analysis, testing, and research, and to monitor and analyze usage and activity trends.</li>
                        </ul>
                    </li>
                    <li><span>Improving the safety and security of our users and services:</span> Shabeb Delivery prioritizes the security of your personal information. We abide by generally accepted standards to secure any personal information submitted to us, both during transmission and once it is received. If you have any questions about the security of your personal information, please contact us at hello@shabeb.com or scroll down to the last section of the Privacy Policy to view our contact information. When you place an express or deferred order through Shabeb, it encrypts the credit/debit card number you submit prior to transmission over the Internet using secure socket layer (SSL) encryption technology. However, no transmission of data over the Internet or any other network can be guaranteed to be 100% secure. Although Shabeb does everything in its power to protect personal information upon receiving it, we cannot warrant the security of information we receive.</li>
                    <li><span>To use many of Shabeb’s services:</span> it will require registration and log-in processes in which you will need to provide your phone number, email, and password. You are encouraged to provide a secure password. It is your responsibility to maintain the confidentiality of that password and all activities that occur while using your password, whether or not authorized by you. You agree to immediately notify us of any unauthorized use of your password or accounts. Background check information is collected from delivery drivers mainly to ensure safe and legal use of Shabeb delivery services as per the Lebanese Law. Historical data is collected to ensure that deliveries under the name of Shabeb are carried out by qualified individuals who have no record of unsafe driving behavior or engagement in any illegal activities as per the Lebanese Law.</li>
                    <li>
                        <p><span>Providing customer support:</span> Shabeb uses all the information submitted by you and with your consent to assist you when you contact us for customer support through any of our contact points (such as email, telephone, and social media). This information gathered is used to:</p>
                        <ul>
                            <li>Direct your questions and concerns to the appropriate Shabeb representative,</li>
                            <li>Investigate and address any of your concerns, and</li>
                            <li>Monitor and continuously improve our customer service.</li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div class="policy">
                <h1>3.Information Sharing</h1>
                <p>Some of Shabeb’s services and features require that we share information with other users or at your request. We may also share your information with our affiliates and business partners for legal issues or in connection with claims or disputes.</p>
                <p>The information Shabeb gathers may be shared with:</p>
                <ul>
                    <li><span>Other users:</span> For example, necessary recipient data (such as address) will be shared with the driver responsible for delivering the order. Date and time of the order will also be shared with the driver to ensure prompt delivery. If you are part of the Shabeb delivery fleet, we may share your information (such as name and photo; vehicle make, model, color, and license plate; location; and contact information [with driver’s consent]) with the recipient (whether the entity be an individual or a business) and/or with the party who placed the order (whether the entity be an individual or a business) for delivery tracking purposes.</li>
                    <li><span>The general public:</span> When you submit content to Shabeb-owned social media (such as Facebook or Instagram in the form of comments or ratings), this content may be viewable by the public through your own consent.</li>
                    <li>
                        <p><span>Shabeb service providers and business partners:</span> These may include:</p>
                        <ul>
                            <li>Payment processors and facilitators,</li>
                            <li>Internal background checkers,</li>
                            <li>Cloud storage providers,</li>
                            <li>Marketing partners and marketing platform providers,</li>
                            <li>Ads and Data analytics partners (see Ads and Analytics Partners below),</li>
                            <li>Vendors that assist Shabeb to maintain and improve its services,</li>
                            <li>Partner vendors.</li>
                        </ul>
                    </li>
                    <li><span>Legal entities:</span> In case of a dispute, Shabeb may share your information if we believe it is required by applicable law, regulation, operating agreement, legal process or governmental request, or where the disclosure is otherwise appropriate due to safety or other similar concerns.</li>
                    <li><span>Your consent:</span> If Shabeb wishes to share your information in any way not aforementioned, we will duly notify you.</li>
                </ul>
            </div>
            <div class="policy">
                <h1>4.Ads and Analytics Partners</h1>
                <p>We use the services of our ads and analytics partners to collect data about customers and their usage of Shabeb services to track bugs, crashes, website usage, and trends. This data is used to enhance our research and development activities, provide better customer service, share relevant ads and promotions with customers, and improve our business processes. Some technologies that we rely on:</p>
                <ul>
                    <li><span>Cookies:</span> A "cookie" is a small data file sent to your browser from a web server and stored on your computer. We use cookies to let us know that you are a prior customer or user and to provide certain features to you. Most web browsers have an option available in the settings to prevent the use of cookies. However, if you disable this feature, certain aspects of Shabeb services may not function properly. We have relationships with third parties who may collect or receive information directly from the cookies placed by our service on your computer to assist in analyzing our business and customers. Shabeb collects and processes data with the use of Google Analytics. To learn more about Google Analytics and how your data is being used, please visit: <a class="break" href="https://policies.google.com/technologies/partner-sites">https://policies.google.com/technologies/partner-sites</a>.</li>
                    <li><span>Action tags:</span> Our service may also use a common web technology called "action tags" (also called single pixel gifs or web beacons) to track purchase, usage and other web activity information. Action tags may be used to provide data to third parties for tracking purposes. Moreover, third parties may set their own cookies and tags through Shabeb. Shabeb has no control over such third parties or their privacy practices. If you do not want third parties to be able to use such methods, you should edit your browser settings to limit such access.</li>
                </ul>
            </div>
            <div class="policy">
                <h1>5.Your Choices</h1>
                <p>You have the option to opt-in to receive Shabeb email notifications. Unless you subscribe and provide your email address, Shabeb will not send you company and account updates. If you are already subscribed to our email notifications, you can opt-out of them at any time by simply clicking ‘Unsubscribe’ at the bottom of the email message. Whether you are subscribed or not, Shabeb may send you important emails regarding transactions on our website or mobile application.</p>
            </div>
            <div class="policy">
                <h1>6.Privacy Updates</h1>
                <p>We may occasionally update this policy. If Shabeb makes any significant changes, we will notify you about the changes through our website and/or by email. After being noticed, you consent to any updates to this policy to the extent permitted under applicable law. We recommend that our users frequently review this Privacy Policy to stay up-to-date on how Shabeb is committed to your privacy. You can request an older version of our Privacy Policy at any time by sending us an email at hello@shabeb.com.</p>
            </div>
            <div class="policy">
                <h1>7.Contact Us</h1>
                <p>We are interested in hearing your feedback about this Privacy Policy. For all questions, concerns, or comments, please email us at hello@shabeb.com. Expect a reply from us within two to three business days.</p>
            </div>
            <div class="modification">
                <p>Shabeb Delivery - Terms of Use Version 1.1</p>
                <h1>Last Modified: <span>April 15, 2020</span></h1>
                <h1>Effective Date: <span>April 17, 2020</span></h1>
            </div>            
        </div>
    </div>
        <Footer />
</template>

<script>

    import Footer from '../components/Footer.vue'
    import Header from '../components/Header.vue'

    export default {
        name: 'Privacy',
        components:{
            Footer,
            Header
        },
    }
</script>

<style scoped>
    .privacy-container{
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 4rem 4rem;
        color: var(--color-primary);
        padding-top: 15rem;
        text-align: justify;
    }
    .privacy-container .title{
        margin: 3rem;
    }
    .privacy-container .title h1{
        font-size: 5rem;
    }
    .privacy-container .info{
        width: 100%;
        padding: 2rem;
    }
    .privacy-container .info h1{
        font-size: 2rem;
        font-weight: 400;
        margin-bottom: 2rem;
    }
    .privacy-container .info ul{
        width: 100%;
        padding: 2rem;
    }
    .privacy-container .info ul li{
        font-size: 1.5rem;
        text-align: justify;
        font-weight: 300;
    }
    .privacy-container .policies{
        position: relative;
        width: 100%;
        padding: 2rem;
        margin-bottom: 2rem;
    }
    .privacy-container a{
        color: var(--accent);
    }
    .privacy-container a:active{
        color: var(--accent);
    }
    .privacy-container a:visited{
        color: var(--accent);
    }
    .privacy-container a:hover{
        color: var(--accent);
    }
    .privacy-container .policies .policy{
        margin-bottom: 3rem;
        position: relative;
        width: 100%;
    }
    .privacy-container .policies .policy span{
        font-weight: 800;
        font-size: 1.7rem;
    }
    .privacy-container .policies .policy h1{
        font-size: 3rem;
        margin-bottom: 1rem;
    }
    .privacy-container .policies .policy p{
        font-size: 1.5rem;
        text-align: justify;
    }
    .privacy-container .policies .policy ul{
        position: relative;
        width: 100%;
        padding: 2rem;
        padding-top: 0;
    }
    .privacy-container .policies .policy ul li{
        font-size: 1.5rem;
        position: relative;
        letter-spacing: .2rem;
        line-height: 1.5;
    }
    .privacy-container .policies .policy ul li ul{
        width: 100%;
        padding: 4rem;
        padding-top: 0;
    }
    .modification{
        width: 100%;
        position: relative;
    }
    .modification p{
        font-style: italic;
        font-size: 1.5rem;
        margin-bottom: 2rem;
    }
    .modification h1{
        font-size: 2rem;
    }
    .modification h1 span{
        font-weight: 300;
    }
    .break{
        word-break: break-all;
    }
    @media screen and (max-width:479px) {
        .privacy-container{
            padding: 1rem;
            padding-top: 15rem;
        }
        .privacy-container .info, .privacy-container .info ul,
        .privacy-container .policies, .privacy-container .policies .policy ul
        {
            padding: 1rem;
        }
        .privacy-container .title h1{
            font-size: 3rem;
        }
    }

</style>